import { autoUpdate, flip, offset, type Placement, size, useFloating } from '@floating-ui/react'

export interface UseFloatingDropdownOptions {
  placement?: Placement
  offsetPx?: number
  enabled?: boolean
}

/**
 * Hook for dropdown positioning using @floating-ui/react.
 * Handles viewport boundary detection and auto-positioning.
 */
export function useFloatingDropdown({
  placement = 'bottom-start',
  offsetPx = 4,
  enabled = true,
}: UseFloatingDropdownOptions = {}) {
  const { refs, floatingStyles, context } = useFloating({
    placement,
    middleware: [
      offset(offsetPx),
      flip({
        fallbackAxisSideDirection: 'end',
        padding: 8,
      }),
      size({
        apply({ rects, elements, availableHeight }) {
          // Match dropdown width to trigger width, and cap its height to whatever
          // room is actually available on the side `flip` picked — without this,
          // a dropdown that flips upward (not enough space below the trigger) can
          // render taller than the space above it and get clipped/cut off instead
          // of shrinking to fit. `.lookup-field__options` fills the rest via flex.
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
            maxHeight: `${Math.max(availableHeight, 0)}px`,
          })
        },
        padding: 8,
      }),
    ],
    whileElementsMounted: enabled ? autoUpdate : undefined,
  })

  return {
    refs,
    floatingStyles,
    context,
  }
}
